import type { ReactElement } from 'react';
import type { RunningNowListItem } from '@kirin/api-client/contracts/shadow-ai-running-now';
import {
  ProcessAncestryTable,
  ProcessDetails,
} from '@/components/shared/running-processes';

export function ExpandedProcessRow({
  row,
}: {
  row: RunningNowListItem;
}): ReactElement {
  const hasExtras = !!row.extras && Object.keys(row.extras).length > 0;
  // KIR-4651: the chain's presence is the only gate. An endpoint running an
  // agent that predates the feature reports none, and the panel then looks
  // exactly as it did before — no flag, no placeholder, no empty block.
  const ancestry = row.ancestry ?? [];
  const showAncestry = ancestry.length > 0;
  // Whether the chain starts at THIS process. A leaf with a different PID is
  // a different process, and suppressing the PID and command lines against it
  // would hide every clue that it is wrong.
  //
  // Not a defence against a recycled PID, despite the shape: recycling reuses
  // the number, so a stale chain's leaf still matches here. That is settled in
  // the upsert, which drops a stored chain once started_at moves
  // (shadow-ai.repository.ts). Comparing startedAt here would only misfire —
  // the row's comes from etime / GetProcessTimes, an ancestor's from
  // time.Unix(StartSec, 0), so they disagree below the second routinely.
  const chainMatchesRow = ancestry[0]?.pid === row.pid;
  // What the block below would actually render, which is not the same question
  // once a chain suppresses three of its lines: counting a command line it has
  // been told to hide leaves an empty bordered box, and ignoring isRoot drops
  // the badge the suppression was written to preserve.
  const hasDetails = chainMatchesRow
    ? !!row.binaryPath || hasExtras || row.isRoot
    : !!row.binaryPath || !!row.commandLine || !!row.parentName || hasExtras;

  if (!hasDetails && !showAncestry) {
    return (
      <span className="text-xs text-muted-foreground pl-8">
        No additional details
      </span>
    );
  }

  return (
    <div className="pl-8 space-y-4">
      {showAncestry && (
        <ProcessAncestryTable ancestry={ancestry} detectedPid={row.pid} />
      )}
      {hasDetails && (
        // Everything the chain already shows is turned off here: its detected
        // row carries this PID and command line, and the row above it is this
        // parent. What is left is what the chain has no column for.
        <ProcessDetails
          process={row}
          showPid={!chainMatchesRow}
          showCommand={!chainMatchesRow}
          showParent={!chainMatchesRow}
        >
          {hasExtras && row.extras ? (
            <>
              {Object.entries(row.extras).map(([k, v]) => (
                <div key={k} className="flex items-baseline gap-1 min-w-0">
                  <span className="text-muted-foreground shrink-0">{k}:</span>
                  <code className="bg-muted px-1 py-0.5 rounded truncate block">
                    {v}
                  </code>
                </div>
              ))}
            </>
          ) : null}
        </ProcessDetails>
      )}
    </div>
  );
}
